<template>
  <v-container class="old-mutual-protect">
    <!-- Header -->
    <v-row no-gutters>
      <v-col cols="12" sm="1">
        <router-link
          :to="{ name: 'OverviewPage', hash: '#insurance' }"
          class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" sm="11">
        <h1 class="mb-2 title-section">
          Tell us about the values of your assets and liabilities
        </h1>
        <p class="mt-3 mb-2">
          No matter who you are, there will be costs associated with your death,
          determined by the assets and liabilities in your estate.
        </p>
        <p>
          By completing the values below, we will provide you with an estimate
          of the estate administration costs related to your estate.
        </p>
      </v-col>
    </v-row>

    <v-row class="calculator-container">
      <!-- ============ LEFT COLUMN — INPUTS ============ -->
      <v-col xl="6" lg="6" md="12" sm="12" xs="12" class="pa-0">
        <v-col cols="12" class="inputs-container">
          <ValidationObserver ref="observer">
            <!-- ===== Personal Assets ===== -->
            <v-card class="w-100 card-container elevation-10 section-card">
              <v-card-text class="calculator-card">
                <h2 class="section-title">Personal Assets</h2>
                <v-row
                  v-for="field in assetFields"
                  :key="field.key"
                  class="align-center calculator-row"
                >
                  <v-col class="calculator-label-column">
                    <h4>{{ field.label }}</h4>
                  </v-col>
                  <v-col class="d-flex align-center calculator-value-column">
                    <ValidationProvider
                      :vid="field.key"
                      v-slot="{ errors }"
                      :name="field.label"
                      rules="required"
                      class="w-100"
                    >
                      <TextField
                        class="calculator-input"
                        prefix="R"
                        :hint="field.hint || ''"
                        :calculator-margins="true"
                        :default-margin="false"
                        :value="randDisplay(store[field.key])"
                        :required="true"
                        :errors="errors"
                        pattern="[0-9\s]*"
                        @keypress="isNumber($event)"
                        @input="onCentsInput(field.key, $event)"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
                <v-row no-gutters class="mt-2">
                  <v-col cols="12">
                    <OmProtectOtherRepeater
                      :items="store.assetOthers"
                      category="asset_other"
                      label="Other assets"
                      @add="addOther('assetOthers')"
                      @remove="index => removeOther('assetOthers', index)"
                      @update="
                        (index, patch) =>
                          updateOther('assetOthers', index, patch)
                      "
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- ===== Liabilities ===== -->
            <v-card class="w-100 card-container elevation-10 section-card">
              <v-card-text class="calculator-card">
                <h2 class="section-title">Liabilities</h2>
                <p class="helper-text">
                  A liability is a debt or loan that is owed to a person,
                  business or organisation. It is compulsory to capture at least
                  one liability.
                </p>
                <v-row
                  v-for="field in liabilityFields"
                  :key="field.key"
                  class="align-center calculator-row"
                >
                  <v-col class="calculator-label-column">
                    <h4>{{ field.label }}</h4>
                  </v-col>
                  <v-col class="d-flex align-center calculator-value-column">
                    <ValidationProvider
                      :vid="field.key"
                      v-slot="{ errors }"
                      :name="field.label"
                      rules="required"
                      class="w-100"
                    >
                      <TextField
                        class="calculator-input"
                        prefix="R"
                        hint=""
                        :calculator-margins="true"
                        :default-margin="false"
                        :value="randDisplay(store[field.key])"
                        :required="true"
                        :errors="errors"
                        pattern="[0-9\s]*"
                        @keypress="isNumber($event)"
                        @input="onCentsInput(field.key, $event)"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
                <v-row no-gutters class="mt-2">
                  <v-col cols="12">
                    <OmProtectOtherRepeater
                      :items="store.liabilityOthers"
                      category="liability_other"
                      label="Other liabilities"
                      @add="addOther('liabilityOthers')"
                      @remove="index => removeOther('liabilityOthers', index)"
                      @update="
                        (index, patch) =>
                          updateOther('liabilityOthers', index, patch)
                      "
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- ===== Insolvency ===== -->
            <v-card class="w-100 card-container elevation-10 section-card">
              <v-card-text class="calculator-card">
                <h2 class="section-title">Insolvency</h2>
                <p class="question-label">
                  Have you ever been declared insolvent?
                </p>
                <div id="insolvency">
                  <ValidationProvider
                    vid="insolvency"
                    v-slot="{ errors }"
                    name="insolvency"
                    rules="required"
                  >
                    <v-radio-group
                      v-model="store.everDeclaredInsolvent"
                      row
                      :error-messages="errors"
                    >
                      <v-radio :value="true" color="primary" label="Yes" />
                      <v-radio :value="false" color="primary" label="No" />
                    </v-radio-group>
                  </ValidationProvider>
                </div>

                <template v-if="store.everDeclaredInsolvent === true">
                  <p class="question-label">
                    Have you been through debt rehabilitation?
                  </p>
                  <div id="debt-rehabilitation">
                    <ValidationProvider
                      vid="debt-rehabilitation"
                      v-slot="{ errors }"
                      name="debt rehabilitation"
                      rules="required"
                    >
                      <v-radio-group
                        v-model="store.debtRehabilitationCompleted"
                        row
                        :error-messages="errors"
                      >
                        <v-radio :value="true" color="primary" label="Yes" />
                        <v-radio :value="false" color="primary" label="No" />
                      </v-radio-group>
                    </ValidationProvider>
                  </div>

                  <v-row
                    v-if="store.debtRehabilitationCompleted === true"
                    no-gutters
                  >
                    <v-col cols="12">
                      <div id="date-of-debt-rehabilitation">
                        <v-menu
                          v-model="debtRehabDateMenu"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template #activator="{ on }">
                            <TextField
                              label="Date of debt rehabilitation"
                              :value="store.debtRehabilitationDate || ''"
                              :readonly="true"
                              :isDateField="true"
                              :errors="dateError ? [dateError] : []"
                              @input="store.debtRehabilitationDate = $event"
                              @date-picker-click="on.click"
                            >
                              <template #prepend v-if="!whitelabel.omDesign">
                                <v-icon>{{ icons.mdiCalendar }}</v-icon>
                              </template>
                            </TextField>
                          </template>
                          <v-date-picker
                            v-model="store.debtRehabilitationDate"
                            :max="currentDate"
                            @input="debtRehabDateMenu = false"
                          />
                        </v-menu>
                      </div>
                    </v-col>
                  </v-row>
                </template>
              </v-card-text>
            </v-card>

            <!-- ===== Existing cover ===== -->
            <v-card class="w-100 card-container elevation-10 section-card">
              <v-card-text class="calculator-card">
                <h2 class="section-title">Existing Cover</h2>
                <p class="helper-text">
                  Please add policies that are considered part of the
                  estate/deemed property, for example a life policy on the life
                  of the deceased due and recoverable on death, regardless of
                  who the owner is. Except for:
                </p>
                <ul class="helper-text">
                  <li>
                    Policies that are taken out for buy and sell purposes*
                  </li>
                  <li>Policies that are taken out for key person purposes**</li>
                  <li>
                    Policies payable to the surviving spouse as the
                    beneficiary***
                  </li>
                </ul>
                <p class="helper-text helper-footnote">
                  * If it meets the requirements of the Estate Duty Act (section
                  3(3)(a)(iA) of the Estate Duty Act)<br />
                  ** If it meets the requirements of the Estate Duty Act
                  (section 3(3)(a)(ii) of the Estate Duty Act)<br />
                  *** These policies are deemed property, but are being excluded
                  under this specific field for purposes of this calculation.
                </p>
                <v-row class="align-center calculator-row">
                  <v-col class="calculator-label-column">
                    <h4>Existing cover (insurance)</h4>
                  </v-col>
                  <v-col class="d-flex align-center calculator-value-column">
                    <ValidationProvider
                      vid="existingCoverCents"
                      v-slot="{ errors }"
                      name="Existing cover (insurance)"
                      rules="required"
                      class="w-100"
                    >
                      <TextField
                        class="calculator-input"
                        prefix="R"
                        hint=""
                        :calculator-margins="true"
                        :default-margin="false"
                        :value="randDisplay(store.existingCoverCents)"
                        :required="true"
                        :errors="errors"
                        pattern="[0-9\s]*"
                        @keypress="isNumber($event)"
                        @input="onCentsInput('existingCoverCents', $event)"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- ===== Deductions ===== -->
            <v-card class="w-100 card-container elevation-10 section-card">
              <v-card-text class="calculator-card">
                <h2 class="section-title">Deductions</h2>
                <v-row
                  v-for="field in deductionFields"
                  :key="field.key"
                  class="align-center calculator-row"
                >
                  <v-col class="calculator-label-column">
                    <h4>{{ field.label }}</h4>
                  </v-col>
                  <v-col class="d-flex align-center calculator-value-column">
                    <ValidationProvider
                      :vid="field.key"
                      v-slot="{ errors }"
                      :name="field.label"
                      rules="required"
                      class="w-100"
                    >
                      <TextField
                        class="calculator-input"
                        prefix="R"
                        hint=""
                        :calculator-margins="true"
                        :default-margin="false"
                        :value="randDisplay(store[field.key])"
                        :required="true"
                        :errors="errors"
                        pattern="[0-9\s]*"
                        @keypress="isNumber($event)"
                        @input="onCentsInput(field.key, $event)"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
                <v-row no-gutters class="mt-2">
                  <v-col cols="12">
                    <OmProtectOtherRepeater
                      :items="store.deductionOthers"
                      category="deduction_other"
                      label="Other deductions"
                      @add="addOther('deductionOthers')"
                      @remove="index => removeOther('deductionOthers', index)"
                      @update="
                        (index, patch) =>
                          updateOther('deductionOthers', index, patch)
                      "
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- ===== Section 4A abatement ===== -->
            <v-card class="w-100 card-container elevation-10 section-card">
              <v-card-text class="calculator-card">
                <h2 class="section-title">Section 4A Abatement</h2>
                <p class="helper-text">
                  A Section 4A abatement, currently R3.5m per individual, is a
                  rebate that is exempt from estate duty, which you can make use
                  of to leave a duty-free inheritance to relatives or friends.
                </p>
                <v-row class="align-center calculator-row">
                  <v-col class="calculator-label-column">
                    <h4>Section 4A abatement</h4>
                  </v-col>
                  <v-col class="d-flex align-center calculator-value-column">
                    <ValidationProvider
                      vid="section4aAbatementCents"
                      v-slot="{ errors }"
                      name="Section 4A abatement"
                      rules="required"
                      class="w-100"
                    >
                      <TextField
                        class="calculator-input"
                        prefix="R"
                        hint=""
                        :calculator-margins="true"
                        :default-margin="false"
                        :value="randDisplay(store.section4aAbatementCents)"
                        :required="true"
                        :errors="errors"
                        pattern="[0-9\s]*"
                        @keypress="isNumber($event)"
                        @input="onCentsInput('section4aAbatementCents', $event)"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- ===== Estate Solutions (desktop only — in-column) ===== -->
            <v-card
              class="w-100 card-container elevation-10 section-card d-none d-lg-block"
            >
              <v-card-text class="calculator-card">
                <h2 class="section-title">Estate Solutions</h2>
                <p class="helper-text">
                  <strong>{{ testator.firstNames }}</strong
                  >, every estate is unique. Your adviser can help you put
                  together a personalised plan to ensure your loved ones are
                  financially supported and that estate-related costs can be
                  covered when the time comes.
                </p>
                <p class="helper-text">
                  Select <strong>"I'm Interested"</strong> below to discuss your
                  options with your adviser, or continue to download your will
                  if you have already made arrangements.
                </p>
                <div class="d-flex flex-wrap mt-4">
                  <div class="mr-3 mb-3">
                    <GradientButton
                      title="Yes I'm Interested"
                      callout="right"
                      :loading="buttonLoadingNext"
                      :disabled="anyButtonLoading"
                      @gradientButtonClicked="interested"
                    />
                  </div>
                  <div class="mb-3">
                    <GradientButton
                      title="No thanks, I'll skip to my will"
                      :fill="false"
                      :callout="null"
                      :loading="buttonLoadingSkipToWill"
                      :disabled="anyButtonLoading"
                      @gradientButtonClicked="skipToWill"
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </ValidationObserver>
        </v-col>
      </v-col>

      <!-- ============ RIGHT COLUMN — RESULTS ============ -->
      <v-col xl="6" lg="6" md="12" sm="12" xs="12" class="pa-0 results-col">
        <v-col
          cols="12"
          class="results-container"
          :style="{
            borderColor: whitelabel.colors.primary,
            backgroundColor: whitelabel.colors.primaryAlpha,
          }"
        >
          <v-card class="card-container elevation-10 section-card">
            <h2 class="section-title results-title">Your Estate Summary</h2>
            <v-row
              class="pl-5 pr-5 calculator-card-header align-center justify-center"
              :style="{ backgroundColor: whitelabel.colors.primary }"
              no-gutters
            >
              <v-col cols="12" class="total-cost-row">
                <h4 class="white--text total-cost-title text-center">
                  Estimated estate administration costs
                </h4>
              </v-col>
              <v-col cols="12" class="total-cost-row">
                <h4
                  class="text-center total-cost-amount"
                  :style="{ color: whitelabel.colors.primary }"
                >
                  R
                  {{
                    (computed.estimatedAdminCostsTotal / 100)
                      | currencyTwoDecimals
                  }}
                </h4>
              </v-col>
              <v-col
                lg="12"
                md="12"
                sm="12"
                class="d-flex align-center justify-content pl-8 pr-8"
                ><span class="white--text">
                  Based on estimates for Estate Duty, Executor's Fees,
                  Conveyancing Fees, and Admin Fees
                </span>
              </v-col>
            </v-row>
            <v-card-text class="calculator-card">
              <template v-for="(row, idx) in resultRows">
                <v-row
                  :key="row.key"
                  class="align-center calculator-cost-row justify-content-between"
                  :class="{
                    'cost-row-bold': row.bold,
                    'cost-row-spaced': row.spaced,
                  }"
                >
                  <v-col lg="7" md="12" sm="12" class="d-flex align-center">
                    <h4 class="calculator-label">{{ row.label }}</h4>
                  </v-col>
                  <v-col
                    lg="5"
                    md="12"
                    sm="12"
                    class="d-flex align-center value-column"
                  >
                    <h4 class="text-right values">
                      {{ row.negative ? '- ' : '' }}R
                      {{ (row.value / 100) | currency }}
                    </h4>
                  </v-col>
                </v-row>
                <hr :key="`hr-${row.key}`" v-if="idx < resultRows.length - 1" />
              </template>
            </v-card-text>
          </v-card>
          <p class="footnote">
            *Estate duty is levied at 20% on the first R30,000,000 of the
            dutiable estate (R6,000,000 maximum), and 25% on any amount
            exceeding R30,000,000.
          </p>
        </v-col>
      </v-col>
    </v-row>

    <!-- ===== Estate Solutions (mobile/tablet only — below results) ===== -->
    <v-row class="d-lg-none mt-4">
      <v-col cols="12">
        <v-card class="w-100 card-container elevation-10 section-card">
          <v-card-text class="calculator-card">
            <h2 class="section-title">Estate Solutions</h2>
            <p class="helper-text">
              <strong>{{ testator.firstNames }}</strong
              >, every estate is unique. Your adviser can help you put together
              a personalised plan to ensure your loved ones are financially
              supported and that estate-related costs can be covered when the
              time comes.
            </p>
            <p class="helper-text">
              Select <strong>"I'm Interested"</strong> below to discuss your
              options with your adviser, or continue to download your will if
              you have already made arrangements.
            </p>
            <div class="d-flex flex-wrap mt-4">
              <div class="mr-3 mb-3">
                <GradientButton
                  title="Yes I'm Interested"
                  callout="right"
                  :loading="buttonLoadingNext"
                  :disabled="anyButtonLoading"
                  @gradientButtonClicked="interested"
                />
              </div>
              <div class="mb-3">
                <GradientButton
                  title="No thanks, I'll skip to my will"
                  :fill="false"
                  :callout="null"
                  :loading="buttonLoadingSkipToWill"
                  :disabled="anyButtonLoading"
                  @gradientButtonClicked="skipToWill"
                />
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import TextField from '@/components/common/components/TextField.vue';
  import GradientButton from '@/components/guest/GradientButton.vue';
  import OmProtectOtherRepeater from './OmProtectOtherRepeater.vue';
  import {
    formatCurrency,
    parseCurrency,
    isNumberCheck,
  } from '@/constants/js/currency-input';
  import { UPDATE_OM_PROTECT_STORE_MUTATION } from '@/constants/graphql/om_protect';
  import { TESTATOR_QUERY } from '@/constants/graphql/testators';
  import { computeOmProtect } from '@/helpers/insurance/om_protect_calculator';
  import { mdiArrowLeft, mdiCalendar } from '@mdi/js';

  export default {
    name: 'OldMutualProtect',
    components: { TextField, GradientButton, OmProtectOtherRepeater },
    props: {
      testator: {
        required: true,
        type: Object,
      },
      buttonLoading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        icons: { mdiArrowLeft, mdiCalendar },
        store: this.initStore(),
        saving: false,
        buttonLoadingNext: false,
        buttonLoadingSkipToWill: false,
        debtRehabDateMenu: false,
        dateError: null,
        assetFields: [
          { key: 'propertiesCents', label: 'Properties' },
          {
            key: 'valuablesCents',
            label: 'Valuables',
            hint: 'For example motor vehicles, jewellery, furniture etc.',
          },
          {
            key: 'investmentsCents',
            label: 'Investments',
            hint: 'For example Shares, Unit Trusts, Bonds etc.',
          },
          {
            key: 'businessInterestCents',
            label: 'Business interest',
            hint: 'The value of holding a share or similar interest in a business entity.',
          },
          { key: 'cashAndSavingsCents', label: 'Cash and savings' },
        ],
        liabilityFields: [
          { key: 'homeLoansCents', label: 'Home loans / mortgage bonds' },
          { key: 'personalLoansCents', label: 'Personal loans' },
          { key: 'creditCardsCents', label: 'Credit cards' },
          { key: 'overdraftFacilitiesCents', label: 'Overdraft facilities' },
        ],
        deductionFields: [
          { key: 'funeralExpensesCents', label: 'Funeral expenses' },
          { key: 'bequestToSpouseCents', label: 'Bequest to spouse' },
        ],
      };
    },
    computed: {
      currentDate() {
        return new Date().toJSON().slice(0, 10);
      },
      anyButtonLoading() {
        return this.buttonLoadingNext || this.buttonLoadingSkipToWill;
      },
      computed() {
        return computeOmProtect({
          ...this.store,
          executorFeePercentage: this.executorFeePercentage,
        });
      },
      executorFeePercentage() {
        const t = this.testator;
        if (
          t?.company?.negotiableExecutorFee &&
          t?.negotiatedExecutorFeePercentage
        ) {
          return t.negotiatedExecutorFeePercentage;
        }
        if (t?.company) return t.company.defaultExecutorFeePercentage;
        return 3.5;
      },
      resultRows() {
        const c = this.computed;
        const nonNeg = v => Math.max(0, v || 0);
        return [
          {
            key: 'totalAssets',
            label: 'Total assets',
            value: nonNeg(c.totalAssets),
          },
          {
            key: 'totalLiabilities',
            label: 'Total liabilities',
            value: nonNeg(c.totalLiabilities),
          },
          { key: 'netAssets', label: 'Net assets', value: nonNeg(c.netAssets) },
          {
            key: 'existingCover',
            label: 'Existing cover',
            value: nonNeg(this.store.existingCoverCents),
          },
          {
            key: 'totalValueOfAssets',
            label: 'Total value of assets',
            value: nonNeg(c.totalValueOfAssets),
            bold: true,
            spaced: true,
          },
          {
            key: 'executorFees',
            label: "Executor's fees",
            value: nonNeg(c.executorFees),
            negative: true,
          },
          {
            key: 'conveyancingFees',
            label: 'Conveyancing fees',
            value: nonNeg(c.conveyancingFees),
            negative: true,
          },
          {
            key: 'adminFees',
            label: 'Admin fees',
            value: nonNeg(c.adminFees),
            negative: true,
          },
          {
            key: 'funeralExpenses',
            label: 'Funeral expenses',
            value: nonNeg(this.store.funeralExpensesCents),
            negative: true,
          },
          {
            key: 'bequestToSpouse',
            label: 'Bequest to spouse',
            value: nonNeg(this.store.bequestToSpouseCents),
            negative: true,
          },
          {
            key: 'totalDeductions',
            label: 'Total deductions',
            value: nonNeg(c.totalDeductions),
            bold: true,
            spaced: true,
            negative: true,
          },
          { key: 'netEstate', label: 'Net estate', value: nonNeg(c.netEstate) },
          {
            key: 'section4aAbatement',
            label: 'Section 4A abatement',
            value: nonNeg(this.store.section4aAbatementCents),
          },
          {
            key: 'dutiableEstate',
            label: 'Dutiable estate',
            value: nonNeg(c.dutiableEstate),
          },
          {
            key: 'estateDuty',
            label: 'Estate Duty',
            value: nonNeg(c.estateDutyPayable),
          },
        ];
      },
    },
    watch: {
      buttonLoading(value) {
        if (!value) {
          this.buttonLoadingNext = false;
          this.buttonLoadingSkipToWill = false;
        }
      },
      'store.everDeclaredInsolvent'(newValue) {
        if (newValue !== true) {
          this.store.debtRehabilitationCompleted = null;
          this.store.debtRehabilitationDate = null;
        }
      },
      'store.debtRehabilitationCompleted'(newValue) {
        if (newValue !== true) {
          this.store.debtRehabilitationDate = null;
        }
        this.dateError = null;
      },
      'store.debtRehabilitationDate'(newValue) {
        if (newValue) this.dateError = null;
      },
    },
    methods: {
      defaultSection4aAbatementCents() {
        const ecc = this.testator && this.testator.estateCostCalculation;
        const rolloverRand =
          ecc && ecc.hasEstateDutyRollover === 'Yes'
            ? ecc.estateDutyRolloverAmount || 0
            : 0;
        return (3500000 + rolloverRand) * 100;
      },
      initStore() {
        const existing = this.testator && this.testator.omProtectStore;
        const blank = {
          propertiesCents: 0,
          valuablesCents: 0,
          investmentsCents: 0,
          businessInterestCents: 0,
          cashAndSavingsCents: 0,
          homeLoansCents: 0,
          personalLoansCents: 0,
          creditCardsCents: 0,
          overdraftFacilitiesCents: 0,
          existingCoverCents: 0,
          funeralExpensesCents: 0,
          bequestToSpouseCents: 0,
          section4aAbatementCents: this.defaultSection4aAbatementCents(),
          everDeclaredInsolvent: null,
          debtRehabilitationCompleted: null,
          debtRehabilitationDate: null,
          assetOthers: [],
          liabilityOthers: [],
          deductionOthers: [],
        };
        if (!existing) return blank;
        return {
          ...blank,
          ...existing,
          assetOthers: (existing.assetOthers || []).map(i => ({ ...i })),
          liabilityOthers: (existing.liabilityOthers || []).map(i => ({
            ...i,
          })),
          deductionOthers: (existing.deductionOthers || []).map(i => ({
            ...i,
          })),
        };
      },
      randDisplay(cents) {
        return formatCurrency((cents || 0) / 100);
      },
      onCentsInput(field, value) {
        const rand = parseCurrency(value) || 0;
        this.store[field] = Math.round(rand * 100);
      },
      isNumber(event) {
        isNumberCheck(event);
      },
      addOther(categoryKey) {
        this.store[categoryKey].push({ description: '', amountCents: 0 });
      },
      removeOther(categoryKey, index) {
        const item = this.store[categoryKey][index];
        if (item.id) {
          this.$set(this.store[categoryKey][index], '_destroy', true);
        } else {
          this.store[categoryKey].splice(index, 1);
        }
      },
      updateOther(categoryKey, index, patch) {
        const current = this.store[categoryKey][index];
        this.$set(this.store[categoryKey], index, { ...current, ...patch });
      },
      async save({ skipped = false, redirect = true } = {}) {
        if (this.saving) return;
        this.saving = true;
        try {
          const isSavable = i =>
            i._destroy || (i.description || '').trim().length > 0;
          const lineItems = [
            ...this.store.assetOthers
              .filter(isSavable)
              .map(i => this.lineItemPayload(i, 'asset_other')),
            ...this.store.liabilityOthers
              .filter(isSavable)
              .map(i => this.lineItemPayload(i, 'liability_other')),
            ...this.store.deductionOthers
              .filter(isSavable)
              .map(i => this.lineItemPayload(i, 'deduction_other')),
          ];
          const cents = k => this.store[k] || 0;
          const input = {
            testatorId: this.testator.id,
            propertiesCents: cents('propertiesCents'),
            valuablesCents: cents('valuablesCents'),
            investmentsCents: cents('investmentsCents'),
            businessInterestCents: cents('businessInterestCents'),
            cashAndSavingsCents: cents('cashAndSavingsCents'),
            homeLoansCents: cents('homeLoansCents'),
            personalLoansCents: cents('personalLoansCents'),
            creditCardsCents: cents('creditCardsCents'),
            overdraftFacilitiesCents: cents('overdraftFacilitiesCents'),
            existingCoverCents: cents('existingCoverCents'),
            funeralExpensesCents: cents('funeralExpensesCents'),
            bequestToSpouseCents: cents('bequestToSpouseCents'),
            section4aAbatementCents: cents('section4aAbatementCents'),
            everDeclaredInsolvent: this.store.everDeclaredInsolvent,
            debtRehabilitationCompleted: this.store.debtRehabilitationCompleted,
            debtRehabilitationDate: this.store.debtRehabilitationDate,
            lineItems,
            skipped,
          };
          await this.$apollo.mutate({
            mutation: UPDATE_OM_PROTECT_STORE_MUTATION,
            variables: { omProtectStoreInput: input },
            update: (cache, { data: { updateOmProtectStore } }) => {
              try {
                const query = {
                  query: TESTATOR_QUERY,
                  variables: { id: this.testator.id },
                };
                const data = cache.readQuery(query);
                data.testator.omProtectStore = updateOmProtectStore;
                cache.writeQuery({ ...query, data });
              } catch {
                // testator query not in cache yet — nothing to patch
              }
            },
          });
          if (redirect) {
            this.$router.push({ name: 'OldMutualProtectConfirm' });
          }
        } catch (e) {
          console.error('Failed to save OM Protect store', e);
          if (!skipped) throw e;
        } finally {
          this.saving = false;
        }
      },
      interested() {
        this.$refs.observer.validate().then(valid => {
          const dateMissing =
            this.store.everDeclaredInsolvent === true &&
            this.store.debtRehabilitationCompleted === true &&
            !this.store.debtRehabilitationDate;
          this.dateError = dateMissing
            ? 'The date of debt rehabilitation field is required'
            : null;

          if (!valid || dateMissing) {
            const errors = this.$refs.observer.ctx.errors;
            let firstErrorField = Object.keys(errors).find(
              field => errors[field].length
            );
            if (!firstErrorField && dateMissing) {
              firstErrorField = 'date-of-debt-rehabilitation';
            }
            if (firstErrorField) {
              const el = document.getElementById(firstErrorField);
              if (el)
                el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
            return;
          }
          this.buttonLoadingNext = true;
          this.save().finally(() => {
            this.buttonLoadingNext = false;
          });
        });
      },
      async skipToWill() {
        this.buttonLoadingSkipToWill = true;
        await this.save({ skipped: true, redirect: false });
        this.$emit('updateInsurance', {
          insuranceInputDetails: {
            testatorId: this.testator.id,
            skipInsurance: true,
          },
          redirectLocation: 'OverviewPage',
        });
      },
      lineItemPayload(item, category) {
        const payload = {
          category,
          description: item.description,
          amountCents: item.amountCents || 0,
        };
        if (item.id) payload.id = item.id;
        if (item._destroy) payload._destroy = true;
        return payload;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .old-mutual-protect {
    .calculator-container {
      margin: 22px 0 0 0 !important;
    }

    h2.section-title {
      font-family: 'Montserrat', sans-serif;
      font-weight: 500;
      font-size: 22px;
      line-height: 26px;
      margin: 0 0 12px 0;
      color: #282828;

      &.results-title {
        padding: 20px 40px 0 40px;
      }
    }

    h4 {
      color: #000;
      font-size: 16px;
      font-weight: 600;

      &.values {
        font-size: 16px;
        font-weight: 400 !important;
      }
    }

    .question-label {
      font-weight: 500;
      margin-bottom: 4px;
    }

    .helper-text {
      font-size: 13px;
      margin-bottom: 12px;
      color: rgba(0, 0, 0, 0.6);

      li {
        font-size: 13px;
      }
    }

    .footnote {
      font-size: 12px;
      margin-top: 16px;
      color: rgba(0, 0, 0, 0.6);
    }

    .inputs-container {
      padding: 8px 28px 30px 28px;
    }

    .results-col {
      position: sticky;
      top: 24px;
      align-self: flex-start;
    }

    .results-container {
      padding: 8px 28px 30px 28px;
      border: 1.5px solid;
      border-radius: 6px;
      margin-top: 8px;

      .card-container {
        margin-top: 16px;
      }
    }

    .card-container {
      border-radius: 4px !important;
      overflow: hidden;
    }

    .section-card {
      margin-bottom: 35px;
    }

    .calculator-card {
      width: 100%;
      padding: 20px 40px;
    }

    .calculator-card-header {
      min-height: 90px;
      margin: 0 !important;
      padding-top: 16px;
      padding-bottom: 8px;
    }

    .total-cost-title {
      font-size: 20px;
      font-weight: bold;
    }

    .total-cost-amount {
      background-color: white;
      border-radius: 14px;
      padding: 11px;
      font-size: 22px;
      font-weight: bold;
      margin: 0;
    }

    .total-cost-row {
      padding: 8px 4px;
    }

    hr {
      border: 0;
      border-top: 1px solid rgba(135, 135, 135, 0.15);
      height: 1px;
      margin: 0;
    }

    .value-column {
      justify-content: flex-end;
    }

    .calculator-row {
      margin-top: 0;
      margin-bottom: 0;

      & + .calculator-row {
        margin-top: 4px;
      }
    }

    .calculator-label-column {
      display: flex;
      align-items: center;
      align-self: flex-start;
      min-height: 60px;
      padding-top: 6px;
      padding-bottom: 6px;

      h4 {
        margin: 0;
        line-height: 1.2;
      }
    }

    .calculator-value-column {
      display: flex;
      align-items: flex-start;
      align-self: flex-start;
      flex-direction: column;
      padding-top: 6px;
      padding-bottom: 6px;

      ::v-deep .omk-text-field-container {
        margin: 0 !important;
        padding: 0 !important;
        width: 100%;
      }

      ::v-deep omk-text-field {
        display: block;
        width: 100%;
      }
    }

    .calculator-input {
      font-size: 16px;
      width: 100%;
      margin: 0 !important;
    }

    .calculator-label {
      font-size: 15px;
      font-weight: 500;
    }

    .calculator-cost-row {
      margin: 0;
    }

    .cost-row-bold {
      .calculator-label,
      .values {
        font-weight: 700 !important;
      }
    }

    .cost-row-spaced {
      padding-top: 10px;
      padding-bottom: 10px;
    }

    @media (max-width: 1263px) {
      .results-col {
        position: static;
      }

      .results-container {
        margin-top: 24px;
      }

      .inputs-container {
        padding: 16px 8px;
      }
    }
  }
</style>
